import { useState, useEffect, useCallback, useRef } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { Menu, X } from 'lucide-react'
import clsx from 'clsx'
import type { MenuProps, NavItem } from '../../core/navigation'
import MenuUserInfo from './MenuUserInfo'

const RADIUS_1 = 150       // Raggio prima corona
const RADIUS_2 = 250       // Raggio seconda corona (children)
const PETAL_SIZE = 52       // Dimensione petalo
const MAX_FIRST = 3         // Max petali visibili nella prima corona
const MAX_SECOND = 4        // Max children visibili nella seconda corona

// Angoli per la prima corona (arco da bottom-left)
const ARC_START = 15
const ARC_END = 75
const CENTER_ANGLE = (ARC_START + ARC_END) / 2  // 45° — centro dell'arco

// Angoli per la seconda corona (sempre centrata a 45°)
const CHILD_SPREAD = 14     // Gradi tra ogni child

function polarToXY(angleDeg: number, radius: number) {
  const angleRad = (angleDeg * Math.PI) / 180
  return {
    x: radius * Math.cos(angleRad),
    y: -radius * Math.sin(angleRad),
  }
}

export default function RadialCoronaMenu({ navigation, user, onLogout }: MenuProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [expandedParent, setExpandedParent] = useState<string | null>(null)
  const [scrollOffset, setScrollOffset] = useState(0)
  const [childScrollOffset, setChildScrollOffset] = useState(0)
  const [showUserPopup, setShowUserPopup] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)
  const navigate = useNavigate()
  const location = useLocation()

  // Close on route change
  useEffect(() => {
    setIsOpen(false)
    setExpandedParent(null)
    setShowUserPopup(false)
  }, [location.pathname])

  // Close on outside click / Escape
  useEffect(() => {
    if (!isOpen) return
    const handleClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setIsOpen(false)
        setExpandedParent(null)
        setShowUserPopup(false)
      }
    }
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (expandedParent) {
          setExpandedParent(null)
          setChildScrollOffset(0)
        } else {
          setIsOpen(false)
          setShowUserPopup(false)
        }
      }
    }
    window.addEventListener('click', handleClick)
    window.addEventListener('keydown', handleEscape)
    return () => {
      window.removeEventListener('click', handleClick)
      window.removeEventListener('keydown', handleEscape)
    }
  }, [isOpen, expandedParent])

  // Mouse wheel scroll
  const handleWheel = useCallback((e: React.WheelEvent) => {
    e.preventDefault()
    const delta = e.deltaY > 0 ? 1 : -1

    if (expandedParent) {
      // Se la seconda corona è aperta, scrolla i children
      const ch = navigation.find(i => i.name === expandedParent)?.children || []
      const maxChildOffset = Math.max(0, ch.length - MAX_SECOND)
      setChildScrollOffset(prev => Math.max(0, Math.min(prev + delta, maxChildOffset)))
    } else {
      // Altrimenti scrolla la prima corona
      const maxOffset = Math.max(0, navigation.length - MAX_FIRST)
      setScrollOffset(prev => Math.max(0, Math.min(prev + delta, maxOffset)))
    }
  }, [expandedParent, navigation])

  // Prima corona: 3 items visibili a partire da scrollOffset
  const visibleItems = navigation.slice(scrollOffset, scrollOffset + MAX_FIRST)

  // Quando espando un parent
  const handlePetalClick = (item: NavItem, globalIndex: number) => {
    if (item.children) {
      if (expandedParent === item.name) {
        setExpandedParent(null)
        setChildScrollOffset(0)
      } else {
        // Centra il parent: mettilo come secondo dei 3 visibili
        const centeredOffset = Math.max(0, Math.min(globalIndex - 1, navigation.length - MAX_FIRST))
        setScrollOffset(centeredOffset)
        setExpandedParent(item.name)
        setChildScrollOffset(0)
      }
    } else if (item.href) {
      navigate(item.href)
    }
  }

  const isActive = (href: string) => {
    if (href === '/app') return location.pathname === '/app'
    return location.pathname.startsWith(href)
  }

  const isGroupActive = (item: NavItem) => {
    if (item.href) return isActive(item.href)
    if (item.children) return item.children.some(c => isActive(c.href))
    return false
  }

  // Calcola angolo per un petalo della prima corona
  // Quando c'è un parent espanso: parent al centro, gli altri ai lati
  const getItemAngle = (item: NavItem, visibleIndex: number): number => {
    if (expandedParent) {
      if (item.name === expandedParent) return CENTER_ANGLE
      // Gli altri item vanno ai lati
      const expandedVisIdx = visibleItems.findIndex(i => i.name === expandedParent)
      if (visibleIndex < expandedVisIdx) return ARC_START
      return ARC_END
    }
    // Nessun parent espanso: distribuzione uniforme
    const count = visibleItems.length
    if (count === 1) return CENTER_ANGLE
    const step = (ARC_END - ARC_START) / (count - 1)
    return ARC_START + visibleIndex * step
  }

  // Seconda corona: SEMPRE centrata a 45° (CENTER_ANGLE)
  const expandedItem = expandedParent ? navigation.find(i => i.name === expandedParent) : null
  const children = expandedItem?.children || []
  const visibleChildren = children.slice(childScrollOffset, childScrollOffset + MAX_SECOND)

  const getChildAngle = (visibleChildIndex: number) => {
    const count = visibleChildren.length
    const totalSpread = (count - 1) * CHILD_SPREAD
    const startAngle = CENTER_ANGLE - totalSpread / 2
    return startAngle + visibleChildIndex * CHILD_SPREAD
  }

  // Indicatori scroll
  const hasMorePrev = scrollOffset > 0
  const hasMoreNext = scrollOffset + MAX_FIRST < navigation.length
  const hasMoreChildPrev = childScrollOffset > 0
  const hasMoreChildNext = childScrollOffset + MAX_SECOND < children.length

  return (
    <div ref={menuRef} className="fixed bottom-0 left-0 z-40" onWheel={isOpen ? handleWheel : undefined}>
      {/* Trigger button - quarter circle */}
      <button
        onClick={(e) => {
          e.stopPropagation()
          setIsOpen(prev => !prev)
          if (isOpen) {
            setExpandedParent(null)
            setShowUserPopup(false)
            setChildScrollOffset(0)
          }
          setScrollOffset(0)
        }}
        className="relative w-14 h-14 flex items-center justify-center transition-all duration-300"
        style={{
          borderRadius: '0 100% 0 0',
          backgroundColor: isOpen ? 'var(--primary-700)' : 'var(--primary-600)',
          color: 'white',
          zIndex: 50,
        }}
      >
        {isOpen ? <X className="w-5 h-5 ml-[-4px] mb-[-4px]" /> : <Menu className="w-5 h-5 ml-[-4px] mb-[-4px]" />}
      </button>

      {/* === PRIMA CORONA === */}
      {isOpen && (
        <>
          {visibleItems.map((item, visibleIndex) => {
            const globalIndex = scrollOffset + visibleIndex
            const angle = getItemAngle(item, visibleIndex)
            const pos = polarToXY(angle, RADIUS_1)
            const active = isGroupActive(item)
            const isExpanded = expandedParent === item.name

            return (
              <div
                key={item.name}
                className="radial-petal absolute"
                style={{
                  bottom: `${-pos.y}px`,
                  left: `${pos.x}px`,
                  transitionDelay: `${visibleIndex * 40}ms`,
                  zIndex: 41,
                }}
              >
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    handlePetalClick(item, globalIndex)
                  }}
                  className={clsx(
                    'group relative flex items-center justify-center rounded-full shadow-lg transition-all',
                    isExpanded && 'ring-2 ring-offset-1 scale-110'
                  )}
                  style={{
                    width: `${PETAL_SIZE}px`,
                    height: `${PETAL_SIZE}px`,
                    backgroundColor: active
                      ? 'var(--primary-600)'
                      : isExpanded
                        ? 'var(--surface-elevated)'
                        : 'var(--surface-card)',
                    color: active ? 'white' : 'var(--text-primary)',
                    border: `1px solid ${active ? 'var(--primary-600)' : 'var(--border-default)'}`,
                  }}
                  title={item.name}
                >
                  <item.icon className="w-5 h-5" />
                  {item.badge && item.badge > 0 && (
                    <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] font-bold w-4 h-4 rounded-full flex items-center justify-center">
                      {item.badge}
                    </span>
                  )}
                  {item.children && (
                    <span className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-1.5 h-1.5 rounded-full"
                      style={{ backgroundColor: isExpanded ? 'var(--primary-500)' : 'var(--text-muted)' }}
                    />
                  )}
                  <span
                    className="absolute left-full ml-2 whitespace-nowrap text-xs px-2 py-1 rounded-lg opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity"
                    style={{
                      backgroundColor: 'var(--surface-elevated)',
                      color: 'var(--text-primary)',
                      border: '1px solid var(--border-default)',
                    }}
                  >
                    {item.name}
                  </span>
                </button>
              </div>
            )
          })}

          {/* Indicatore scroll: pallini */}
          {navigation.length > MAX_FIRST && (
            <div className="absolute bottom-1 left-16 flex items-center gap-1" style={{ zIndex: 41 }}>
              {hasMorePrev && (
                <span className="w-1 h-1 rounded-full" style={{ backgroundColor: 'var(--text-muted)' }} />
              )}
              {visibleItems.map((_, i) => (
                <span key={i} className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: 'var(--primary-500)' }} />
              ))}
              {hasMoreNext && (
                <span className="w-1 h-1 rounded-full" style={{ backgroundColor: 'var(--text-muted)' }} />
              )}
            </div>
          )}
        </>
      )}

      {/* === SECONDA CORONA (children) — sempre centrata a 45° === */}
      {isOpen && expandedItem && children.length > 0 && (
        <>
          {visibleChildren.map((child, visibleChildIndex) => {
            const angle = getChildAngle(visibleChildIndex)
            const pos = polarToXY(angle, RADIUS_2)
            const active = isActive(child.href)

            return (
              <div
                key={child.href}
                className="radial-petal absolute"
                style={{
                  bottom: `${-pos.y}px`,
                  left: `${pos.x}px`,
                  transitionDelay: `${visibleChildIndex * 30}ms`,
                  zIndex: 42,
                }}
              >
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    navigate(child.href)
                  }}
                  className="group relative flex items-center justify-center rounded-full shadow-lg transition-colors"
                  style={{
                    width: `${PETAL_SIZE - 6}px`,
                    height: `${PETAL_SIZE - 6}px`,
                    backgroundColor: active ? 'var(--primary-600)' : 'var(--surface-card)',
                    color: active ? 'white' : 'var(--text-primary)',
                    border: `1px solid ${active ? 'var(--primary-600)' : 'var(--border-default)'}`,
                  }}
                  title={child.name}
                >
                  <child.icon className="w-4 h-4" />
                  <span
                    className="absolute left-full ml-2 whitespace-nowrap text-xs px-2 py-1 rounded-lg opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity"
                    style={{
                      backgroundColor: 'var(--surface-elevated)',
                      color: 'var(--text-primary)',
                      border: '1px solid var(--border-default)',
                    }}
                  >
                    {child.name}
                  </span>
                </button>
              </div>
            )
          })}

          {/* Indicatore scroll children */}
          {children.length > MAX_SECOND && (() => {
            const indicatorPos = polarToXY(CENTER_ANGLE, RADIUS_2 + 30)
            return (
              <div
                className="absolute flex items-center gap-1"
                style={{
                  bottom: `${-indicatorPos.y}px`,
                  left: `${indicatorPos.x}px`,
                  zIndex: 42,
                }}
              >
                {hasMoreChildPrev && (
                  <span className="w-1 h-1 rounded-full" style={{ backgroundColor: 'var(--text-muted)' }} />
                )}
                {visibleChildren.map((_, i) => (
                  <span key={i} className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: 'var(--primary-400)' }} />
                ))}
                {hasMoreChildNext && (
                  <span className="w-1 h-1 rounded-full" style={{ backgroundColor: 'var(--text-muted)' }} />
                )}
              </div>
            )
          })()}
        </>
      )}

      {/* User button (sopra il trigger) */}
      {isOpen && (
        <div
          className="absolute bottom-16 left-1"
          style={{ zIndex: 43, transitionDelay: '200ms' }}
        >
          <div className="relative">
            <button
              onClick={(e) => {
                e.stopPropagation()
                setShowUserPopup(prev => !prev)
              }}
              className="w-10 h-10 rounded-full flex items-center justify-center shadow-lg transition-transform hover:scale-110"
              style={{
                backgroundColor: 'var(--primary-600)',
                color: 'white',
              }}
              title={user?.username || 'Account'}
            >
              <span className="text-sm font-medium">
                {user?.email?.charAt(0).toUpperCase()}
              </span>
            </button>
            {showUserPopup && (
              <div
                className="absolute bottom-full left-0 mb-2 rounded-xl shadow-xl p-3"
                style={{
                  backgroundColor: 'var(--surface-card)',
                  border: '1px solid var(--border-default)',
                  minWidth: '200px',
                }}
              >
                <MenuUserInfo user={user} onLogout={onLogout} />
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
